import React, { Component } from "reactn";
import { Icon } from "@material-ui/core";
import styled from "styled-components";

import Api from "js/core/api";
import { trackActivity, trackState } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import { CustomerType } from "common/constants";
import { _ } from "js/vendor";
import { FeatureType } from "js/core/models/features";

import Loadable from "js/react/components/Loadable";
import { Gap10, Gap20 } from "js/react/components/Gap";
import TeamMembersList from "js/react/views/UserOptions/components/TeamMembersList";
import {
    Section,
    UIPane,
    UIPaneContents,
    UIPaneHeader,
    BlueButton
} from "js/react/components/UiComponents";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";

import { TextInput } from "../components";
import { InviteTeamMembersDialog } from "../dialogs";
import AddExistingMemberDialog from "../dialogs/AddExistingMemberDialog";
import ManageAvailableTeamSeatsDialog from "../dialogs/ManageAvailableTeamSeatsDialog";
import { getUsedSeatCount } from "../UserOptionsContainer";
import BillingDataService from "../dataservices/billingDataService";

const SeatCount = styled.div`
  font-size: 14px;
  margin-top: 20px;       
  font-weight: 700;  
`;

class ManageTeamPane extends Component {
    constructor(props) {
        super(props);

        this.state = {
            org: {},
            isLoading: true,
            team: false,
            usedSeatCount: 0,
            userRole: "",
            transactionHistoryData: [],
            subscription: { items: { data: [{ quantity: 0 }] } },
            paymentMethod: null,
            canManageTeam: app.user.features.isFeatureEnabled(FeatureType.MANAGE_TEAM, props.organizationId),
        };

        this.contentsContainerRef = React.createRef();
    }

    get orgTotalSeatCount() {
        const { subscription } = this.state;
        return subscription?.items?.data[0].quantity ?? 0;
    }

    get isActiveSubscription() {
        let { subscription } = this.state;
        return subscription && !subscription.cancel_at_period_end;
    }

    async componentDidMount() {
        await this.getFreshState();
    }

    async componentDidUpdate(prevProps) {
        // org id can change if user changes workspace.
        // isDefaultTeam can change if owner switches from viewing a specific team to all team members view.
        if (this.props.organizationId !== prevProps.organizationId || prevProps.isDefaultTeam !== this.props.isDefaultTeam) {
            this.setState({ org: {}, isLoading: true });
            await this.getFreshState();
            this.setState({ isLoading: false });
        }
    }

    fetchSubscription = async () => {
        const { organizationId } = this.props;
        const [paymentMethodsResponse, subscription, customer] = await Promise.all([
            Api.paymentMethods.get({
                customer_type: CustomerType.TEAM,
                organization_id: organizationId
            }),
            BillingDataService.fetchSubscription(organizationId),
            BillingDataService.fetchCustomer(organizationId),
        ]);

        this.setState({
            subscription,
            paymentMethod: paymentMethodsResponse.body[0],
            team: {
                ...this.state.team,
                name: subscription?.customerName ?? customer.name
            }
        });
    }

    getFreshState = async () => {
        const { isDefaultTeam } = this.props;
        if (isDefaultTeam) {
            const orgResponse = await Api.organizations.get({ id: this.props.organizationId });
            this.setState({ org: orgResponse });
        }
        await this.getTeam();
        if (this.state.canManageTeam) {
            await this.fetchSubscription();
        }
        this.setState({
            isLoading: false
        });
    }

    getTeam = async () => {
        const { teamId } = this.props;
        const teamResponse = await Api.teams.get({ id: teamId });
        const teamInvitesResponse = await Api.teamInvites.get({
            type: "team",
            id: teamId
        });
        const allMembers = teamResponse.body.members.concat(teamInvitesResponse.body);
        const user = allMembers.find(member => member.id === app.user.id);
        this.setUsedSeatCount(allMembers);

        this.setState({
            originalName: teamResponse.body.name,
            team: {
                ...teamResponse.body,
                members: allMembers
            },
            userRole: user ? user.role : "",
        });
    }

    handleTeamNameChange = event => {
        const name = event.target.value;
        this.setState(prevState => ({
            team: {
                ...prevState.team,
                name
            }
        }));
    }

    handleTeamNameBlurred = async (teamId, teamName) => {
        const { handleTeamNameChange, isDefaultTeam, organizationId } = this.props;
        const { originalName } = this.state;
        const trimmedTeamName = teamName.trim();
        const trimmedOriginalName = originalName.trim();
        if (trimmedTeamName === trimmedOriginalName) {
            return;
        }
        const updatedTeam = ds.teams.find(team => team.id == teamId);
        //if an owner is part of a team the team will be part of ds.teams otherwise an API call will be made to change
        //the team name. Need to check for existence of updatedTeam in case the owner is not part of the team
        if (updatedTeam && updatedTeam.get("isDefaultTeam")) {
            Api.organizations.put({ id: organizationId }, { type: "change_name", teamId, teamName: trimmedTeamName });
            const theme = ds.sharedThemes.findWhere({ id: updatedTeam.get("sharedThemeId") });
            theme.update({ name: trimmedTeamName });
        } else if (updatedTeam) {
            updatedTeam.update({ name: trimmedTeamName });
        } else {
            await Api.teams.put({ type: "change_team_name", teamId, teamName: trimmedTeamName });
        }
        if (isDefaultTeam) {
            const props = {
                workspace_id: organizationId,
                old_organization_name: trimmedOriginalName,
                new_organization_name: trimmedTeamName
            };
            trackState({
                "organization_name": trimmedTeamName
            });
            trackActivity("Organization", "NameChanged", null, null, props, { audit: true });
        } else {
            const props = {
                workspace_id: organizationId,
                team_id: teamId,
                old_team_name: trimmedOriginalName,
                new_team_name: trimmedTeamName
            };
            trackActivity("OrgTeam", "Renamed", null, null, props, { audit: true });
        }
        this.setState(prevState => {
            return {
                ...prevState,
                team: {
                    ...prevState.team,
                    name: trimmedTeamName
                }
            };
        });

        if (isDefaultTeam && handleTeamNameChange) {
            handleTeamNameChange(teamName);

            await Api.subscriptions.put({
                teamName,
                customerType: CustomerType.TEAM,
                orgId: organizationId,
                type: "update_customer_name",
            });
        }
    }

    setUsedSeatCount = teamMembers => {
        const usedSeatCount = getUsedSeatCount(teamMembers);
        this.setState({ usedSeatCount });
    }

    handleShowAddMemberDialog = () => {
        const { team, usedSeatCount, org } = this.state;
        const { isDefaultTeam, organizationId } = this.props;
        const AddMemberDialog = isDefaultTeam ? InviteTeamMembersDialog : AddExistingMemberDialog;
        const props = {};
        if (isDefaultTeam) {
            props.usedSeatCount = usedSeatCount;
            props.org = org;
        }
        ShowDialog(AddMemberDialog, {
            organizationId,
            team,
            callback: () => this.getFreshState(),
            ...props
        });
    }

    handleRemoveAvailableSeats = async () => {
        const { usedSeatCount } = this.state;
        const { organizationId } = this.props;

        await BillingDataService.removeTeamSeats(organizationId, usedSeatCount);
        await this.getFreshState();
    }

    handleShowManageAvailableSeatsDialog = () => {
        const { usedSeatCount } = this.state;

        ShowDialog(ManageAvailableTeamSeatsDialog, {
            unusedSeatCount: this.orgTotalSeatCount - usedSeatCount,
            showAddMemberDialog: this.handleShowAddMemberDialog,
            removeAvailableSeats: this.handleRemoveAvailableSeats
        });
    }

    renderSeatCount = () => {
        const { usedSeatCount, canManageTeam, paymentMethod } = this.state;

        // if this.isActiveSubscription is undefined, it means we do not have an active subscription
        // and we should not show the seat count
        if (usedSeatCount == this.orgTotalSeatCount || this.isActiveSubscription === undefined) {
            return (<FlexBox left>
                You have no available Team Pro seats.&nbsp;
                {canManageTeam && this.isActiveSubscription && paymentMethod && <a onClick={this.handleShowAddMemberDialog}>Add Team Pro Seats</a>}
                {canManageTeam && this.isActiveSubscription && !paymentMethod && <a href="mailto:support@beautiful.ai">Contact us to add more seats</a>}
            </FlexBox>);
        }

        return (<FlexBox left>
            {this.orgTotalSeatCount - usedSeatCount} of {this.orgTotalSeatCount} Team Pro {"seat".pluralize(this.orgTotalSeatCount > 1)} available.
            {canManageTeam && this.isActiveSubscription && paymentMethod && <a onClick={this.handleShowManageAvailableSeatsDialog}>Manage seats</a>}
        </FlexBox>);
    }

    render() {
        const { handleRemoveMember, isDefaultTeam, paneClicked } = this.props;
        const { isLoading, team, canManageTeam, org, usedSeatCount, paymentMethod } = this.state;

        const hasAvailableSeats = (this.orgTotalSeatCount - usedSeatCount) > 0;

        return (
            <UIPane className="manage-team">
                <UIPaneHeader>
                    Manage Team
                </UIPaneHeader>
                <UIPaneContents containerRef={this.contentsContainerRef}>
                    <Loadable isLoading={isLoading}>
                        <Section>
                            <Gap10 />
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <div>
                                    <TextInput
                                        label={isDefaultTeam ? "ORGANIZATION NAME" : "TEAM NAME"}
                                        id="team-name"
                                        curValue={team.name}
                                        handleChange={event => this.handleTeamNameChange(event)}
                                        handleBlur={() => this.handleTeamNameBlurred(team.id, team.name)}
                                        disabled={!canManageTeam}
                                        maxLength={50}
                                    />
                                    {isDefaultTeam && canManageTeam && <SeatCount>{this.renderSeatCount()}</SeatCount>}
                                </div>
                                {isDefaultTeam && canManageTeam && (paymentMethod || hasAvailableSeats) && this.isActiveSubscription &&
                                    <BlueButton
                                        id="add-member-button"
                                        style={{ height: 44 }}
                                        onClick={this.handleShowAddMemberDialog}
                                    >
                                        <Icon>add</Icon>
                                        Add a member
                                    </BlueButton>
                                }
                                {!isDefaultTeam && canManageTeam &&
                                    <BlueButton
                                        id="add-member-button"
                                        style={{ height: 44 }}
                                        onClick={this.handleShowAddMemberDialog}
                                    >
                                        <Icon>add</Icon>
                                        Add members to this team
                                    </BlueButton>
                                }
                            </div>
                        </Section>
                        <Gap20 />
                        <TeamMembersList
                            isActiveSubscription={this.isActiveSubscription}
                            organizationId={this.props.organizationId}
                            org={org}
                            team={team}
                            getTeam={this.getTeam}
                            handleRemoveMember={handleRemoveMember}
                            getFreshState={this.getFreshState}
                            totalSeatCount={this.orgTotalSeatCount}
                            isDefaultTeam={isDefaultTeam}
                            canManageTeam={canManageTeam}
                            paneClicked={paneClicked}
                            scrollContainerEl={this.contentsContainerRef.current}
                            canAddSeats={paymentMethod || hasAvailableSeats}
                        />
                    </Loadable>
                </UIPaneContents>
            </UIPane>
        );
    }
}

export default ManageTeamPane;
